import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { toast } from 'sonner';
import { Loader2, Users, DollarSign, TrendingUp, Building2, Eye, Check, X, Sparkles, Calculator, FileSearch, MessageSquare } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../components/ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { getAuthToken } from '../utils/auth';
import BrokenLinkResender from '../components/admin/BrokenLinkResender';
import RigReadyFunnelTab from '../components/admin/RigReadyFunnelTab';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const AdminDashboard = () => {
  const navigate = useNavigate();
  const [isLoading, setIsLoading] = useState(true);
  const [stats, setStats] = useState(null);
  const [dealers, setDealers] = useState([]);
  const [payments, setPayments] = useState([]);
  const [selectedDealer, setSelectedDealer] = useState(null);
  const [showDealerModal, setShowDealerModal] = useState(false);
  const [activeTab, setActiveTab] = useState(() => {
    try {
      const t = new URLSearchParams(window.location.search).get('tab');
      return ['dealers', 'payments', 'free-tools', 'rigready'].includes(t) ? t : 'dealers';
    } catch { return 'dealers'; }
  });
  const [freeToolData, setFreeToolData] = useState(null);
  const [freeToolLoading, setFreeToolLoading] = useState(false);

  const TOOL_LABELS = {
    cost_calculator: { name: 'Cost Calculator', Icon: Calculator, color: 'text-green-600' },
    deal_analyzer: { name: 'Deal Analyzer', Icon: FileSearch, color: 'text-blue-600' },
    negotiation_coach: { name: 'Negotiation Coach', Icon: MessageSquare, color: 'text-purple-600' },
  };

  const fetchFreeToolData = async () => {
    setFreeToolLoading(true);
    try {
      const token = getAuthToken();
      const res = await axios.get(`${API}/admin/free-tool-downloads`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      setFreeToolData(res.data);
    } catch (err) {
      toast.error('Failed to load free tool downloads');
    } finally {
      setFreeToolLoading(false);
    }
  };

  useEffect(() => {
    if (activeTab === 'free-tools' && !freeToolData) {
      fetchFreeToolData();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeTab]);

  useEffect(() => {
    checkAuth();
  }, []);

  const checkAuth = async () => {
    const token = getAuthToken();
    if (!token) {
      navigate('/auth');
      return;
    }

    try {
      const response = await axios.get(`${API}/auth/me`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      if (response.data.role !== 'super_admin') {
        toast.error('Access denied. Super admin only.');
        navigate('/');
        return;
      }

      fetchData();
    } catch (error) {
      console.error('Auth error:', error);
      navigate('/auth');
    }
  };

  const fetchData = async () => {
    const token = getAuthToken();
    try {
      const [statsRes, dealersRes, paymentsRes] = await Promise.all([
        axios.get(`${API}/admin/stats`, { headers: { Authorization: `Bearer ${token}` } }),
        axios.get(`${API}/admin/dealers`, { headers: { Authorization: `Bearer ${token}` } }),
        axios.get(`${API}/admin/payments`, { headers: { Authorization: `Bearer ${token}` } })
      ]);

      setStats(statsRes.data);
      setDealers(dealersRes.data);
      setPayments(paymentsRes.data);
    } catch (error) {
      console.error('Error fetching data:', error);
      toast.error('Failed to load dashboard data');
    } finally {
      setIsLoading(false);
    }
  };

  const viewDealer = async (dealer) => {
    const token = getAuthToken();
    try {
      const response = await axios.get(`${API}/admin/dealers/${dealer.id}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setSelectedDealer(response.data);
      setShowDealerModal(true);
    } catch (error) {
      console.error('Error fetching dealer:', error);
      toast.error('Failed to load dealer details');
    }
  };

  const updateSubscription = async (dealerId, status) => {
    const token = getAuthToken();
    try {
      await axios.patch(
        `${API}/admin/dealers/${dealerId}/subscription?status=${status}`,
        {},
        { headers: { Authorization: `Bearer ${token}` } }
      );
      toast.success('Subscription updated');
      fetchData();
      setShowDealerModal(false);
    } catch (error) {
      console.error('Error updating subscription:', error);
      toast.error('Failed to update subscription');
    }
  };

  if (isLoading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <Loader2 className="animate-spin text-[#004E86]" size={48} />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50 py-12" data-testid="admin-dashboard">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Header */}
        <div className="mb-8 flex items-start justify-between gap-4 flex-wrap">
          <div>
            <h1 
              className="text-4xl md:text-5xl font-bold uppercase tracking-tight text-[#004E86] mb-2"
              style={{fontFamily: 'Barlow Condensed, sans-serif'}}
            >
              Super Admin Dashboard
            </h1>
            <p className="text-lg text-gray-700">Manage dealers and view platform statistics</p>
          </div>
          <Button
            onClick={() => navigate('/admin/commerce')}
            className="bg-[#004E86] hover:bg-[#003E6D] text-white font-bold inline-flex items-center gap-2"
            data-testid="open-commerce-center-btn"
          >
            <DollarSign size={16} /> Commerce Center
          </Button>
        </div>

        {/* Stats */}
        {stats && (
          <div className="grid grid-cols-1 md:grid-cols-4 gap-6 mb-8">
            <div className="bg-white rounded-lg shadow-md p-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-gray-600">Total Dealers</p>
                  <p className="text-3xl font-bold text-[#004E86]">{stats.total_dealers}</p>
                </div>
                <Building2 className="text-[#F2A900]" size={40} />
              </div>
            </div>

            <div className="bg-white rounded-lg shadow-md p-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-gray-600">Active Subscriptions</p>
                  <p className="text-3xl font-bold text-green-600">{stats.active_dealers}</p>
                </div>
                <Check className="text-green-500" size={40} />
              </div>
            </div>

            <div className="bg-white rounded-lg shadow-md p-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-gray-600">Total Leads</p>
                  <p className="text-3xl font-bold text-[#004E86]">{stats.total_leads}</p>
                </div>
                <Users className="text-[#F2A900]" size={40} />
              </div>
            </div>

            <div className="bg-white rounded-lg shadow-md p-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-gray-600">Total Revenue</p>
                  <p className="text-3xl font-bold text-green-600">${(stats.total_revenue ?? 0).toFixed(2)}</p>
                </div>
                <DollarSign className="text-green-500" size={40} />
              </div>
            </div>
          </div>
        )}

        {/* Broken email-link resender */}
        <div className="mb-6">
          <BrokenLinkResender isDark={false} />
        </div>

        {/* Tabs */}
        <div className="mb-6 border-b border-gray-200">
          <div className="flex gap-4">
            <button
              onClick={() => setActiveTab('dealers')}
              className={`pb-3 px-1 border-b-2 font-semibold transition-colors ${
                activeTab === 'dealers'
                  ? 'border-[#004E86] text-[#004E86]'
                  : 'border-transparent text-gray-500 hover:text-gray-700'
              }`}
            >
              Dealers ({dealers.length})
            </button>
            <button
              onClick={() => setActiveTab('payments')}
              className={`pb-3 px-1 border-b-2 font-semibold transition-colors ${
                activeTab === 'payments'
                  ? 'border-[#004E86] text-[#004E86]'
                  : 'border-transparent text-gray-500 hover:text-gray-700'
              }`}
            >
              Payments ({payments.length})
            </button>
            <button
              onClick={() => setActiveTab('free-tools')}
              className={`pb-3 px-1 border-b-2 font-semibold transition-colors flex items-center gap-1.5 ${
                activeTab === 'free-tools'
                  ? 'border-[#004E86] text-[#004E86]'
                  : 'border-transparent text-gray-500 hover:text-gray-700'
              }`}
              data-testid="admin-tab-free-tools"
            >
              <Sparkles size={14} />
              Free Tools
              {freeToolData?.summary?.total_unlocks ? ` (${freeToolData.summary.total_unlocks})` : ''}
            </button>
            <button
              onClick={() => setActiveTab('rigready')}
              className={`pb-3 px-1 border-b-2 font-semibold transition-colors flex items-center gap-1.5 ${
                activeTab === 'rigready'
                  ? 'border-[#004E86] text-[#004E86]'
                  : 'border-transparent text-gray-500 hover:text-gray-700'
              }`}
              data-testid="admin-tab-rigready"
            >
              <TrendingUp size={14} />
              Rig Ready Funnel
            </button>
          </div>
        </div>

        {/* Rig Ready / TowIQ anonymous funnel analytics */}
        {activeTab === 'rigready' && <RigReadyFunnelTab />}

        {/* Advisors Table */}
        {activeTab === 'dealers' && (
          <div className="bg-white rounded-lg shadow-md overflow-hidden">
            <div className="overflow-x-auto">
              <table className="min-w-full divide-y divide-gray-200">
                <thead className="bg-gray-50">
                  <tr>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Business</th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Email</th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Slug</th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Status</th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Plan</th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Created</th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Actions</th>
                  </tr>
                </thead>
                <tbody className="bg-white divide-y divide-gray-200">
                  {dealers.map((dealer) => (
                    <tr key={dealer.id} className="hover:bg-gray-50">
                      <td className="px-4 py-3 whitespace-nowrap font-medium text-gray-900">
                        {dealer.business_name}
                      </td>
                      <td className="px-4 py-3 whitespace-nowrap text-sm text-gray-600">
                        {dealer.email}
                      </td>
                      <td className="px-4 py-3 whitespace-nowrap text-sm text-gray-600">
                        /d/{dealer.slug}
                      </td>
                      <td className="px-4 py-3 whitespace-nowrap">
                        <span className={`px-2 py-1 rounded-full text-xs font-medium ${
                          dealer.subscription_status === 'active' ? 'bg-green-100 text-green-800' :
                          dealer.subscription_status === 'cancelled' ? 'bg-red-100 text-red-800' :
                          'bg-gray-100 text-gray-800'
                        }`}>
                          {dealer.subscription_status || 'inactive'}
                        </span>
                      </td>
                      <td className="px-4 py-3 whitespace-nowrap text-sm text-gray-600">
                        {dealer.subscription_plan || '-'}
                      </td>
                      <td className="px-4 py-3 whitespace-nowrap text-sm text-gray-600">
                        {new Date(dealer.created_at).toLocaleDateString()}
                      </td>
                      <td className="px-4 py-3 whitespace-nowrap">
                        <Button
                          size="sm"
                          variant="outline"
                          onClick={() => viewDealer(dealer)}
                          className="p-1"
                        >
                          <Eye size={16} />
                        </Button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        )}

        {/* Payments Table */}
        {activeTab === 'payments' && (
          <div className="bg-white rounded-lg shadow-md overflow-hidden">
            <div className="overflow-x-auto">
              <table className="min-w-full divide-y divide-gray-200">
                <thead className="bg-gray-50">
                  <tr>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Payment ID</th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Dealer ID</th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Plan</th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Amount</th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Status</th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Date</th>
                  </tr>
                </thead>
                <tbody className="bg-white divide-y divide-gray-200">
                  {payments.map((payment) => (
                    <tr key={payment.id} className="hover:bg-gray-50">
                      <td className="px-4 py-3 whitespace-nowrap text-sm font-mono text-gray-600">
                        {payment.payment_id?.substring(0, 20)}...
                      </td>
                      <td className="px-4 py-3 whitespace-nowrap text-sm text-gray-600">
                        {payment.dealer_id?.substring(0, 8)}...
                      </td>
                      <td className="px-4 py-3 whitespace-nowrap text-sm text-gray-900 capitalize">
                        {payment.plan}
                      </td>
                      <td className="px-4 py-3 whitespace-nowrap text-sm font-bold text-green-600">
                        ${(payment.amount ?? 0).toFixed(2)}
                      </td>
                      <td className="px-4 py-3 whitespace-nowrap">
                        <span className={`px-2 py-1 rounded-full text-xs font-medium ${
                          payment.status === 'completed' ? 'bg-green-100 text-green-800' :
                          payment.status === 'pending' ? 'bg-yellow-100 text-yellow-800' :
                          'bg-red-100 text-red-800'
                        }`}>
                          {payment.status}
                        </span>
                      </td>
                      <td className="px-4 py-3 whitespace-nowrap text-sm text-gray-600">
                        {new Date(payment.created_at).toLocaleDateString()}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        )}

        {/* Free Tools Tab */}
        {activeTab === 'free-tools' && (
          <div className="space-y-4" data-testid="admin-free-tools-panel">
            {freeToolLoading && (
              <div className="bg-white rounded-lg shadow-md p-8 text-center">
                <Loader2 className="animate-spin mx-auto text-[#004E86]" size={28} />
              </div>
            )}

            {freeToolData && (
              <>
                <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                  <div className="bg-white rounded-lg shadow-md p-4">
                    <div className="text-xs uppercase font-semibold text-gray-500 mb-1">Total Unlocks</div>
                    <div className="text-2xl font-bold text-[#004E86]" data-testid="ft-total-unlocks">
                      {freeToolData.summary.total_unlocks}
                    </div>
                  </div>
                  <div className="bg-white rounded-lg shadow-md p-4">
                    <div className="text-xs uppercase font-semibold text-gray-500 mb-1">Unique Emails</div>
                    <div className="text-2xl font-bold text-[#004E86]" data-testid="ft-unique-emails">
                      {freeToolData.summary.total_unique_emails}
                    </div>
                  </div>
                  <div className="bg-white rounded-lg shadow-md p-4">
                    <div className="text-xs uppercase font-semibold text-gray-500 mb-1">Converted to Paid</div>
                    <div className="text-2xl font-bold text-green-600" data-testid="ft-converted">
                      {freeToolData.summary.converted_to_paid}
                    </div>
                  </div>
                  <div className="bg-white rounded-lg shadow-md p-4">
                    <div className="text-xs uppercase font-semibold text-gray-500 mb-1">Conv. Rate</div>
                    <div className="text-2xl font-bold text-green-600">
                      {freeToolData.summary.total_unique_emails > 0
                        ? `${((freeToolData.summary.converted_to_paid / freeToolData.summary.total_unique_emails) * 100).toFixed(1)}%`
                        : '—'}
                    </div>
                  </div>
                </div>

                <div className="bg-white rounded-lg shadow-md p-4">
                  <h3 className="text-sm font-bold uppercase text-gray-700 mb-3">Per-Tool Breakdown</h3>
                  <div className="grid sm:grid-cols-3 gap-3">
                    {['cost_calculator', 'deal_analyzer', 'negotiation_coach'].map((slug) => {
                      const meta = TOOL_LABELS[slug];
                      const Icon = meta.Icon;
                      const stats = freeToolData.summary.per_tool.find((t) => t.slug === slug) || { downloads: 0, unique_users: 0 };
                      return (
                        <div key={slug} className="border border-gray-200 rounded-lg p-3" data-testid={`ft-tool-${slug}`}>
                          <div className="flex items-center gap-2 mb-2">
                            <Icon size={16} className={meta.color} />
                            <span className="font-semibold text-sm">{meta.name}</span>
                          </div>
                          <div className="grid grid-cols-2 gap-1 text-xs">
                            <div>
                              <div className="text-gray-500">Downloads</div>
                              <div className="text-lg font-bold">{stats.downloads}</div>
                            </div>
                            <div>
                              <div className="text-gray-500">Unique users</div>
                              <div className="text-lg font-bold">{stats.unique_users}</div>
                            </div>
                          </div>
                          <code className="block mt-2 text-[10px] bg-gray-50 px-2 py-1 rounded text-gray-600 font-mono break-all">
                            /free/{slug.replace('_', '-')}
                          </code>
                        </div>
                      );
                    })}
                  </div>
                </div>

                <div className="bg-white rounded-lg shadow-md overflow-hidden">
                  <div className="p-4 border-b border-gray-100">
                    <h3 className="text-sm font-bold uppercase text-gray-700">Recent Unlocks</h3>
                  </div>
                  <div className="overflow-x-auto">
                    <table className="min-w-full divide-y divide-gray-200">
                      <thead className="bg-gray-50">
                        <tr>
                          <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">When</th>
                          <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Email</th>
                          <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Tool</th>
                          <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Source</th>
                        </tr>
                      </thead>
                      <tbody className="bg-white divide-y divide-gray-200">
                        {freeToolData.events.slice(0, 100).map((ev) => {
                          const meta = TOOL_LABELS[ev.tool_slug] || { name: ev.tool_slug, Icon: Sparkles, color: 'text-gray-500' };
                          const Icon = meta.Icon;
                          return (
                            <tr key={ev.id} className="hover:bg-gray-50">
                              <td className="px-4 py-2 whitespace-nowrap text-xs text-gray-600">
                                {new Date(ev.created_at).toLocaleString()}
                              </td>
                              <td className="px-4 py-2 whitespace-nowrap text-sm font-medium text-gray-900">
                                {ev.email}
                              </td>
                              <td className="px-4 py-2 whitespace-nowrap">
                                <span className={`inline-flex items-center gap-1.5 text-sm ${meta.color}`}>
                                  <Icon size={14} /> {meta.name}
                                </span>
                              </td>
                              <td className="px-4 py-2 whitespace-nowrap text-xs text-gray-500 font-mono">
                                {ev.source_link || 'direct'}
                              </td>
                            </tr>
                          );
                        })}
                      </tbody>
                    </table>
                    {freeToolData.events.length === 0 && (
                      <div className="text-center py-8 text-sm text-gray-500">No unlocks yet.</div>
                    )}
                  </div>
                </div>
              </>
            )}
          </div>
        )}
      </div>

      {/* Dealer Details Modal */}
      <Dialog open={showDealerModal} onOpenChange={setShowDealerModal}>
        <DialogContent className="max-w-2xl">
          <DialogHeader>
            <DialogTitle className="text-2xl font-bold text-[#004E86]" style={{fontFamily: 'Barlow Condensed, sans-serif'}}>
              Dealer Details
            </DialogTitle>
          </DialogHeader>
          {selectedDealer && (
            <div className="space-y-6">
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <p className="text-sm font-semibold text-gray-500">Business Name</p>
                  <p className="text-lg">{selectedDealer.dealer.business_name}</p>
                </div>
                <div>
                  <p className="text-sm font-semibold text-gray-500">Email</p>
                  <p className="text-lg">{selectedDealer.dealer.email}</p>
                </div>
                <div>
                  <p className="text-sm font-semibold text-gray-500">Quiz Link</p>
                  <code className="text-sm bg-gray-100 px-2 py-1 rounded">/d/{selectedDealer.dealer.slug}</code>
                </div>
                <div>
                  <p className="text-sm font-semibold text-gray-500">Lead Count</p>
                  <p className="text-lg font-bold text-[#004E86]">{selectedDealer.lead_count}</p>
                </div>
                <div>
                  <p className="text-sm font-semibold text-gray-500">Subscription Status</p>
                  <span className={`px-2 py-1 rounded-full text-sm font-medium ${
                    selectedDealer.dealer.subscription_status === 'active' ? 'bg-green-100 text-green-800' : 'bg-gray-100 text-gray-800'
                  }`}>
                    {selectedDealer.dealer.subscription_status || 'inactive'}
                  </span>
                </div>
                <div>
                  <p className="text-sm font-semibold text-gray-500">Plan</p>
                  <p className="text-lg capitalize">{selectedDealer.dealer.subscription_plan || 'None'}</p>
                </div>
              </div>

              <div className="border-t pt-4">
                <p className="text-sm font-semibold text-gray-500 mb-2">Manage Subscription</p>
                <div className="flex gap-2">
                  <Button
                    onClick={() => updateSubscription(selectedDealer.dealer.id, 'active')}
                    className="bg-green-600 hover:bg-green-700 text-white"
                  >
                    Activate
                  </Button>
                  <Button
                    onClick={() => updateSubscription(selectedDealer.dealer.id, 'cancelled')}
                    variant="destructive"
                  >
                    Cancel
                  </Button>
                </div>
              </div>

              {selectedDealer.payments.length > 0 && (
                <div className="border-t pt-4">
                  <p className="text-sm font-semibold text-gray-500 mb-2">Payment History</p>
                  <div className="space-y-2">
                    {selectedDealer.payments.map((payment) => (
                      <div key={payment.id} className="flex justify-between items-center bg-gray-50 p-2 rounded">
                        <span className="capitalize">{payment.plan}</span>
                        <span className="font-bold text-green-600">${(payment.amount ?? 0).toFixed(2)}</span>
                        <span className="text-sm text-gray-500">{new Date(payment.created_at).toLocaleDateString()}</span>
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default AdminDashboard;
